// =============================================================================
// CatalogueDetailPage — model detail at /catalogue/:id
// =============================================================================
// A model (e.g. "Euroflex") with its size variants. Pick a size → see that
// variant's price, barcode and stock, then add it to the order. Made-to-order
// models (local mattresses) surface colour + side-colour options that are
// chosen when the order is created.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, getApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { ProductPlaceholder } from '../../components/ProductPlaceholder';
import { cn } from '../../lib/cn';
import { cart, useCart } from '../../lib/cart';
import { grossToNet } from '../../lib/vat';
import { ModelImagesManager } from './ModelImagesManager';
import { CustomSizeModal } from './CustomSizeModal';

interface StockLevel {
  id: string;
  onHand: number;
  reserved: number;
  display: number; // showroom floor pieces — present but not sellable
  minThreshold: number;
  location: { id: string; name: string; kind: 'warehouse' | 'shop'; shopId: string | null };
}
interface Variant {
  id: string;
  sku: string;
  barcode: string | null;
  size: string | null;
  fabric: string | null;
  color: string | null;
  sideColor: string | null;
  density: string | null;
  name: string;
  basePrice: string;
  salePrice: string | null;
  vatRate: string;
  isActive: boolean;
  stockLevels: StockLevel[];
}
interface ProductModelDetail {
  id: string;
  name: string;
  description: string | null;
  gift: string | null;
  // A gift is either a fixed variant (productId + sku) or a whole model
  // (giftModelId + sizeOptions) whose size the sales girl picks at order time.
  gifts: {
    id: string;
    productId: string | null;
    giftModelId: string | null;
    name: string;
    sku: string | null;
    qty: number;
    available: number | null;
    sizeOptions:
      | { productId: string; sku: string; size: string | null; available: number | null }[]
      | null;
  }[];
  warranty: string | null;
  attrs: Record<string, unknown>;
  colorOptions: string[];
  sideColorOptions: string[];
  fabricOptions: string[];
  densityOptions: string[];
  allowsCustomSize: boolean;
  category: {
    id: string;
    name: string;
    code: string;
    fulfillmentStrategy: string;
    deliveryRequired: boolean;
    tracksStockPerLocation: boolean;
  };
  images: { id: string; url: string; isPrimary: boolean }[];
  variants: Variant[];
}

const strategyLabel = (s: string): string =>
  ({
    STOCK_AT_LOCATION: 'In stock',
    MADE_TO_ORDER_FACTORY: 'Made to order',
    MADE_TO_ORDER_SUPPLIER: 'From supplier',
  })[s] ?? s;

const strategyTone = (s: string): 'positive' | 'info' | 'warning' | 'accent' => {
  switch (s) {
    case 'STOCK_AT_LOCATION':
      return 'positive';
    case 'MADE_TO_ORDER_FACTORY':
      return 'info';
    case 'MADE_TO_ORDER_SUPPLIER':
      return 'warning';
    default:
      return 'accent';
  }
};

const SWATCH: Record<string, string> = {
  blue: '#2f5b8f',
  brown: '#6b4a2b',
  red: '#9b2d2d',
  grena: '#6e1b2f',
  gris: '#8a8a8a',
  blanc: '#f3f1ec',
  white: '#f3f1ec',
  black: '#222',
  noir: '#222',
  beige: '#d8cdb8',
};
const swatch = (c: string): string => SWATCH[c.toLowerCase()] ?? '#bbb';

const uniq = <T,>(arr: T[]): T[] => [...new Set(arr)];

export function CatalogueDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user, perms } = useAuth();
  const { items } = useCart();
  const canEdit = perms.has('products.edit');

  const model = useQuery({
    queryKey: ['product-model', id],
    queryFn: () => apiGet<ProductModelDetail>(`/product-models/${id}`),
  });

  const [params] = useSearchParams();
  const sizeParam = params.get('size');
  const location = useLocation();
  const backSearch = (location.state as { backSearch?: string } | null)?.backSearch;
  const backTo = backSearch ? `/catalogue?${backSearch}` : '/catalogue';
  const [activeImage, setActiveImage] = useState(0);
  // Variants vary along up to four PRICED axes: size, fabric, colour and side
  // colour. The chosen tuple resolves to one Product variant with its own SKU.
  const [selSize, setSelSize] = useState<string | null>(null);
  const [selFabric, setSelFabric] = useState<string | null>(null);
  const [selAxisColor, setSelAxisColor] = useState<string | null>(null);
  const [selAxisSide, setSelAxisSide] = useState<string | null>(null);
  const [selAxisDensity, setSelAxisDensity] = useState<string | null>(null);
  // FREE order-time finishes (same price), shown only when the model exposes them
  // as options (i.e. the axis is NOT priced). Editable again on the order.
  const [selColor, setSelColor] = useState<string | null>(null);
  const [selSide, setSelSide] = useState<string | null>(null);
  // Made-to-order fabric/density are order-time choices (model.fabricOptions /
  // densityOptions), distinct from the priced variant-axis pickers above which
  // resolve a stocked SKU.
  const [selOrderFabric, setSelOrderFabric] = useState<string | null>(null);
  const [selOrderDensity, setSelOrderDensity] = useState<string | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const initedRef = useRef(false);

  const m = model.data;
  const variants = useMemo(() => m?.variants.filter((v) => v.isActive) ?? [], [m]);
  const sizes = useMemo(
    () => uniq(variants.map((v) => v.size).filter((s): s is string => !!s)),
    [variants],
  );
  const fabrics = useMemo(
    () => uniq(variants.map((v) => v.fabric).filter((f): f is string => !!f)),
    [variants],
  );
  const axisColors = useMemo(
    () => uniq(variants.map((v) => v.color).filter((c): c is string => !!c)),
    [variants],
  );
  const axisSides = useMemo(
    () => uniq(variants.map((v) => v.sideColor).filter((c): c is string => !!c)),
    [variants],
  );
  const axisDensities = useMemo(
    () => uniq(variants.map((v) => v.density).filter((d): d is string => !!d)),
    [variants],
  );
  const hasSizes = sizes.length > 0;
  const hasFabrics = fabrics.length > 0;
  const hasColorAxis = axisColors.length > 0;
  const hasSideAxis = axisSides.length > 0;
  const hasDensityAxis = axisDensities.length > 0;
  // The five priced axes as one uniform list, so availability + picking work the
  // same in EVERY direction (size→fabric→colour and colour→fabric→size alike).
  type AxisKey = 'size' | 'fabric' | 'color' | 'sideColor' | 'density';
  const axisDefs: Array<{
    key: AxisKey;
    label: string;
    has: boolean;
    sel: string | null;
    set: (v: string | null) => void;
  }> = [
    { key: 'size', label: 'size', has: hasSizes, sel: selSize, set: setSelSize },
    { key: 'fabric', label: 'fabric', has: hasFabrics, sel: selFabric, set: setSelFabric },
    { key: 'color', label: 'colour', has: hasColorAxis, sel: selAxisColor, set: setSelAxisColor },
    { key: 'sideColor', label: 'side colour', has: hasSideAxis, sel: selAxisSide, set: setSelAxisSide },
    { key: 'density', label: 'density', has: hasDensityAxis, sel: selAxisDensity, set: setSelAxisDensity },
  ];
  const presentAxes = axisDefs.filter((a) => a.has);

  // A value is available when some variant carries it AND matches every OTHER
  // axis the user has selected (unselected axes don't constrain anything).
  const isAvailable = (key: AxisKey, value: string): boolean =>
    variants.some(
      (v) =>
        v[key] === value &&
        presentAxes.every((a) => a.key === key || a.sel == null || v[a.key] === a.sel),
    );

  // One click handler for every axis chip. Re-clicking the selected value
  // unselects it (removing that constraint). Picking a value keeps the other
  // axes where they're still compatible and swaps any that aren't for the first
  // value that fits — so a dimmed chip is still clickable and simply switches
  // the rest of the combination over. Explicitly-unselected axes stay empty.
  const pickAxis = (key: AxisKey, value: string | null): void => {
    const me = axisDefs.find((a) => a.key === key)!;
    if (value == null || me.sel === value) {
      me.set(null);
      return;
    }
    me.set(value);
    let pool = variants.filter((v) => v[key] === value);
    for (const a of presentAxes) {
      if (a.key === key || a.sel == null) continue;
      const keep = pool.some((v) => v[a.key] === a.sel)
        ? a.sel
        : (pool.map((v) => v[a.key]).find((x): x is string => !!x) ?? null);
      if (keep !== a.sel) a.set(keep);
      pool = pool.filter((v) => v[a.key] === keep);
    }
  };

  // Each present axis must match; an absent axis (e.g. a model with no fabrics)
  // doesn't constrain the lookup.
  const selected =
    variants.find(
      (v) =>
        (!hasSizes || v.size === selSize) &&
        (!hasFabrics || v.fabric === selFabric) &&
        (!hasColorAxis || v.color === selAxisColor) &&
        (!hasSideAxis || v.sideColor === selAxisSide) &&
        (!hasDensityAxis || v.density === selAxisDensity),
    ) ?? null;
  // The shop she's currently working from — highlight its stock first ("at your
  // shop"), following the nav switcher rather than her permanent home shop.
  const homeShopId = user?.activeShopId ?? user?.homeShopId ?? null;

  // Initialise the axes once the model loads: honour ?size= from the catalogue
  // card, else default to the first available combination.
  useEffect(() => {
    if (initedRef.current || !m || variants.length === 0) return;
    initedRef.current = true;
    const first = (sizeParam ? variants.find((v) => v.size === sizeParam) : null) ?? variants[0];
    if (!first) return;
    setSelSize(first.size);
    setSelFabric(first.fabric);
    setSelAxisColor(first.color);
    setSelAxisSide(first.sideColor);
    setSelAxisDensity(first.density);
  }, [m, variants, sizeParam]);

  // Show the availability panel for any stock-tracked category (incl. made-to-
  // order finished goods held as reserved). Out-of-stock never blocks the sale:
  // made-to-order raises a job, and a stocked item with nothing available goes
  // into the cart as a reservation — the New Order page flags the line as a
  // backorder and it allocates when stock arrives. Quotations stay stock-only.
  const stockTracked = !!m && m.category.tracksStockPerLocation;
  const sellFromStock = !!m && m.category.fulfillmentStrategy === 'STOCK_AT_LOCATION';

  const sortedStock = useMemo(() => {
    const list = [...(selected?.stockLevels ?? [])];
    return list.sort((a, b) => {
      const aHome = a.location.shopId === homeShopId ? 0 : 1;
      const bHome = b.location.shopId === homeShopId ? 0 : 1;
      if (aHome !== bHome) return aHome - bHome;
      if (a.location.kind !== b.location.kind) return a.location.kind === 'warehouse' ? -1 : 1;
      return a.location.name.localeCompare(b.location.name);
    });
  }, [selected, homeShopId]);

  const attrEntries = useMemo(
    () => Object.entries(m?.attrs ?? {}).filter(([, v]) => v != null && v !== ''),
    [m?.attrs],
  );

  if (model.isLoading)
    return (
      <PageScaffold back={{ to: backTo, label: 'Catalogue' }}>
        <div className="text-center text-ink-3 py-20">Loading…</div>
      </PageScaffold>
    );
  if (model.isError || !m) {
    return (
      <PageScaffold back={{ to: backTo, label: 'Catalogue' }}>
        <Card>
          <EmptyState
            title="Model not found"
            description="It may have been removed or you don't have access."
            action={
              <Link to="/catalogue">
                <Button variant="secondary">← Back to catalogue</Button>
              </Link>
            }
          />
        </Card>
      </PageScaffold>
    );
  }

  const price = selected ? Number(selected.salePrice ?? selected.basePrice) : 0;
  const onSale = !!selected?.salePrice;
  const priceUnset = !selected || price <= 0;
  const inCart = selected ? items.find((l) => l.productId === selected.id) : undefined;
  const homeShopLevel = sortedStock.find((l) => l.location.shopId === homeShopId);
  const totalOnHand = sortedStock.reduce((s, l) => s + Math.max(0, l.onHand - l.reserved - l.display), 0);
  // Stocked item with nothing available anywhere → sold as a backorder reservation.
  const outOfStockEverywhere = sellFromStock && totalOnHand === 0;
  const hasColours =
    m.colorOptions.length > 0 ||
    m.sideColorOptions.length > 0 ||
    m.fabricOptions.length > 0 ||
    m.densityOptions.length > 0;
  // A made-to-order item (supplier/factory) that we happen to hold available stock
  // of is sold straight from stock — delivered now, no PO/production wait. Only
  // when the variant's SKU fully pins the finish: a model with FREE finish options
  // (colour/fabric/side/density chosen at order time) isn't stocked per-finish, so
  // whether it ships from stock depends on the order-time pick — the backend makes
  // the final call at confirm. Surface the path on the pill + note when it's safe.
  const deliverFromStock = !sellFromStock && !!selected && totalOnHand > 0 && !hasColours;

  const onAdd = (): void => {
    if (!selected || priceUnset) return;
    cart.add({
      productId: selected.id,
      sku: selected.sku,
      name: selected.name,
      unitPrice: Number(selected.salePrice ?? selected.basePrice),
      vatRate: Number(selected.vatRate),
      imageUrl: m.images[0]?.url ?? null,
      categoryName: m.category.name,
      colorOptions: m.colorOptions,
      sideColorOptions: m.sideColorOptions,
      fabricOptions: m.fabricOptions,
      densityOptions: m.densityOptions,
      selectedColor: selColor,
      selectedSideColor: selSide,
      selectedFabric: selOrderFabric,
      selectedDensity: selOrderDensity,
      deliveryRequired: m.category.deliveryRequired,
      fulfillmentStrategy: m.category.fulfillmentStrategy,
      // Preview the model's free gifts in the order cart, carrying their live
      // stock so the cart can warn when a gift is short (backend still skips OOS).
      // Model gifts carry their size options so the sales girl picks a size later.
      gifts: m.gifts.map((g) => ({
        productId: g.productId,
        giftModelId: g.giftModelId,
        sku: g.sku,
        name: g.name,
        qty: g.qty,
        available: g.available,
        sizeOptions: g.sizeOptions,
      })),
    });
  };

  const img = m.images[activeImage] ?? m.images[0] ?? null;

  return (
    <PageScaffold back={{ to: backTo, label: 'Catalogue' }} contentClassName="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Gallery */}
        <div className="lg:col-span-3 space-y-3">
          <div className="aspect-[4/3] bg-paper-2 rounded-lg overflow-hidden border border-rule relative">
            {img ? (
              <img src={img.url} alt={m.name} className="w-full h-full object-cover" />
            ) : (
              <ProductPlaceholder />
            )}
            <div className="absolute top-4 left-4">
              {deliverFromStock ? (
                <StatusPill tone="positive">In stock — deliver now</StatusPill>
              ) : (
                <StatusPill tone={strategyTone(m.category.fulfillmentStrategy)}>
                  {strategyLabel(m.category.fulfillmentStrategy)}
                </StatusPill>
              )}
            </div>
          </div>
          {m.images.length > 1 && (
            <div className="grid grid-cols-6 gap-2">
              {m.images.map((image, idx) => (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => setActiveImage(idx)}
                  className={cn(
                    'aspect-square rounded-md overflow-hidden border-2 transition',
                    idx === activeImage ? 'border-accent' : 'border-transparent hover:border-rule',
                  )}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={image.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {canEdit && <ModelImagesManager modelId={m.id} images={m.images} />}
        </div>

        {/* Info + variant picker */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-3 mb-1">
              {m.category.name}
            </div>
            <h1 className="kc-serif text-4xl text-ink leading-tight">{m.name}</h1>
          </div>

          {/* Size picker */}
          {hasSizes && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium mb-2">
                Size <span className="text-ink-4">· {sizes.length} available</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => {
                  const dimmed = s !== selSize && !isAvailable('size', s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => pickAxis('size', s)}
                      title={
                        s === selSize
                          ? 'Click to unselect'
                          : dimmed
                            ? 'Not in this combination — click to switch the other options'
                            : undefined
                      }
                      className={cn(
                        'px-3 py-2 rounded border text-sm transition kc-tnum',
                        s === selSize
                          ? 'bg-accent text-white border-accent'
                          : dimmed
                            ? 'bg-paper-2 text-ink-4 border-rule-2 opacity-50 hover:opacity-100 hover:border-accent/40'
                            : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40 hover:text-ink',
                      )}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Fabric picker — second variant axis. Fabrics that don't exist with
              the currently selected size/colour are dimmed but stay clickable:
              picking one switches the incompatible axes over. */}
          {hasFabrics && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium mb-2">
                Fabric <span className="text-ink-4">· {fabrics.length} available</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {fabrics.map((f) => {
                  const dimmed = f !== selFabric && !isAvailable('fabric', f);
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => pickAxis('fabric', f)}
                      title={
                        f === selFabric
                          ? 'Click to unselect'
                          : dimmed
                            ? 'Not in this combination — click to switch the other options'
                            : undefined
                      }
                      className={cn(
                        'px-3 py-2 rounded border text-sm transition',
                        f === selFabric
                          ? 'bg-accent text-white border-accent'
                          : dimmed
                            ? 'bg-paper-2 text-ink-4 border-rule-2 opacity-50 hover:opacity-100 hover:border-accent/40'
                            : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40 hover:text-ink',
                      )}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Priced colour axis — each value is its own SKU/price (vs the free
              "Choose your finish" swatches further down for non-priced models). */}
          {hasColorAxis && (
            <Swatches
              label="Colour"
              options={axisColors}
              value={selAxisColor}
              onChange={(v) => pickAxis('color', v)}
              isUnavailable={(v) => !isAvailable('color', v)}
            />
          )}
          {hasSideAxis && (
            <Swatches
              label="Side colour"
              options={axisSides}
              value={selAxisSide}
              onChange={(v) => pickAxis('sideColor', v)}
              isUnavailable={(v) => !isAvailable('sideColor', v)}
            />
          )}
          {hasDensityAxis && (
            <Swatches
              label="Density"
              options={axisDensities}
              value={selAxisDensity}
              onChange={(v) => pickAxis('density', v)}
              isUnavailable={(v) => !isAvailable('density', v)}
            />
          )}

          {variants.length === 0 && (
            <div className="text-sm text-ink-3 italic">No sizes configured yet.</div>
          )}
          {variants.length > 0 &&
            !selected &&
            (presentAxes.some((a) => a.sel == null) ? (
              <div className="text-sm text-ink-3">
                Select{' '}
                {presentAxes
                  .filter((a) => a.sel == null)
                  .map((a) => a.label)
                  .join(', ')}{' '}
                to see the price and stock.
              </div>
            ) : (
              <div className="text-sm text-warning">
                This combination isn't available — pick another.
              </div>
            ))}

          {/* Selected variant price + meta */}
          {selected && (
            <div className="pb-5 border-b border-rule">
              <div className="flex items-baseline gap-3 flex-wrap">
                {priceUnset ? (
                  <span className="kc-serif text-3xl text-ink-3 italic">Price on request</span>
                ) : (
                  <>
                    <Money
                      value={selected.salePrice ?? selected.basePrice}
                      className="kc-serif text-4xl text-ink"
                    />
                    {onSale && (
                      <Money
                        value={selected.basePrice}
                        className="text-base text-ink-4 line-through"
                      />
                    )}
                  </>
                )}
              </div>
              {!priceUnset && (
                <div className="text-[11px] text-ink-3 mt-1">
                  excl. VAT{' '}
                  <Money
                    value={String(grossToNet(price, Number(selected.vatRate)))}
                    className="text-ink-2"
                  />
                </div>
              )}
              <div className="text-[10px] uppercase tracking-wider text-ink-3 mt-1.5">
                {!priceUnset && <>VAT {(Number(selected.vatRate) * 100).toFixed(0)}% included · </>}
                Code <span className="kc-mono">{selected.sku}</span>
                {selected.fabric && (
                  <>
                    {' '}
                    · Fabric <span className="text-ink-2">{selected.fabric}</span>
                  </>
                )}
                {selected.barcode && (
                  <>
                    {' '}
                    · Barcode <span className="kc-mono">{selected.barcode}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Linked free gifts — offered (opt-in) on the order/quote line & deducted
              from stock when the sales girl adds them. Edit in the admin model wizard. */}
          {m.gifts.length > 0 && (
            <div className="rounded-md border border-accent/30 bg-accent-soft/40 p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">
                🎁 Free gift available
              </div>
              <div className="text-sm text-ink mt-1 space-y-0.5">
                {m.gifts.map((g) => {
                  // A model gift offers several sizes (chosen at order time), so it
                  // has no single stock figure — show the size count instead.
                  if (g.giftModelId) {
                    const n = g.sizeOptions?.length ?? 0;
                    return (
                      <div key={g.id} className="flex items-center justify-between gap-3">
                        <span>{g.qty} × {g.name}</span>
                        <span className="text-xs text-ink-3">
                          {n} size{n === 1 ? '' : 's'} — choose at sale
                        </span>
                      </div>
                    );
                  }
                  const short = g.available != null && g.available < g.qty;
                  return (
                    <div key={g.id} className="flex items-center justify-between gap-3">
                      <span>{g.qty} × {g.name}</span>
                      <span className={`text-xs ${short ? 'text-negative' : 'text-ink-3'}`}>
                        {g.available == null
                          ? 'made to order'
                          : short
                            ? `only ${g.available} in stock`
                            : `${g.available} in stock`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Free-gift note + warranty — editable in-app by users with products.edit. */}
          {(m.gift || canEdit) && (
            <EditableModelField
              modelId={m.id}
              field="gift"
              label="🎁 Gift note"
              value={m.gift}
              canEdit={canEdit}
              placeholder="e.g. free pillows with purchase"
            />
          )}
          {(m.warranty || canEdit) && (
            <EditableModelField
              modelId={m.id}
              field="warranty"
              label="🛡 Warranty"
              value={m.warranty}
              canEdit={canEdit}
              placeholder="e.g. 10 Years on Springs"
            />
          )}

          {/* Choose finish (made-to-order). Selectable; still editable on the order. */}
          {hasColours && (
            <div className="rounded-md border border-rule bg-paper-2/40 p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium">
                Choose your finish
              </div>
              {m.colorOptions.length > 0 && (
                <Swatches
                  label="Mattress colour"
                  options={m.colorOptions}
                  value={selColor}
                  onChange={setSelColor}
                />
              )}
              {m.sideColorOptions.length > 0 && (
                <Swatches
                  label="Side colour"
                  options={m.sideColorOptions}
                  value={selSide}
                  onChange={setSelSide}
                />
              )}
              {m.fabricOptions.length > 0 && (
                <Swatches
                  label="Fabric"
                  options={m.fabricOptions}
                  value={selOrderFabric}
                  onChange={setSelOrderFabric}
                />
              )}
              {m.densityOptions.length > 0 && (
                <Swatches
                  label="Density"
                  options={m.densityOptions}
                  value={selOrderDensity}
                  onChange={setSelOrderDensity}
                />
              )}
              <div className="text-[11px] text-ink-3">
                You can still change the finish on the order.
              </div>
            </div>
          )}

          {/* Stock for the selected size */}
          {stockTracked && selected && (
            <div className="rounded-md border border-rule bg-paper-2/40 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium">
                  Availability · {selected.size ?? selected.sku}
                </span>
                {homeShopLevel ? (
                  <span className="text-sm text-ink">
                    <span className="kc-mono kc-tnum font-medium">
                      {Math.max(0, homeShopLevel.onHand - homeShopLevel.reserved - homeShopLevel.display)}
                    </span>{' '}
                    <span className="text-ink-3">at your shop</span>
                  </span>
                ) : totalOnHand > 0 ? (
                  <span className="text-sm text-ink">
                    <span className="kc-mono kc-tnum font-medium">{totalOnHand}</span>{' '}
                    <span className="text-ink-3">across locations</span>
                  </span>
                ) : null}
              </div>
              {sortedStock.length === 0 ? (
                <div className="text-xs text-ink-3 italic">No stock records for this size.</div>
              ) : (
                <ul className="divide-y divide-rule-2 text-sm">
                  {sortedStock.map((s) => {
                    const isHome = s.location.shopId === homeShopId;
                    // Sellable units — a reserved or showroom-display piece is
                    // physically there but can't be sold off the shelf.
                    const sellable = Math.max(0, s.onHand - s.reserved - s.display);
                    const low = sellable <= s.minThreshold;
                    const out = sellable === 0;
                    return (
                      <li
                        key={s.id}
                        className={cn(
                          'flex items-center justify-between py-1.5',
                          isHome && 'font-medium',
                        )}
                      >
                        <span className={cn(isHome ? 'text-ink' : 'text-ink-2')}>
                          {s.location.name}
                          {isHome && (
                            <span className="ml-2 text-[9px] uppercase tracking-wider text-accent">
                              ← here
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            'kc-mono kc-tnum',
                            out ? 'text-negative' : low ? 'text-warning' : 'text-ink',
                          )}
                        >
                          {sellable}
                          {s.reserved > 0 && (
                            <span className="ml-1 text-[10px] text-ink-3 normal-case lowercase">
                              · {s.reserved} reserved
                            </span>
                          )}
                          {s.display > 0 && (
                            <span className="ml-1 text-[10px] text-ink-3 normal-case lowercase">
                              · {s.display} on display
                            </span>
                          )}
                          {out && <span className="ml-1 text-[10px] uppercase">out</span>}
                          {!out && low && <span className="ml-1 text-[10px] uppercase">low</span>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {m.category.fulfillmentStrategy === 'MADE_TO_ORDER_FACTORY' && !deliverFromStock && (
            <div className="rounded-md border border-info/30 bg-info-soft p-3 text-xs text-ink-2">
              <span className="font-medium text-info uppercase tracking-wider text-[10px]">
                Made to order
              </span>
              <p className="mt-1">
                Manufactured in our factory after the order is confirmed. Pick a delivery date that
                gives production enough headroom.
              </p>
            </div>
          )}
          {deliverFromStock && (
            <div className="rounded-md border border-positive/30 bg-positive-soft p-3 text-xs text-ink-2">
              <span className="font-medium text-positive uppercase tracking-wider text-[10px]">
                In stock
              </span>
              <p className="mt-1">
                We hold this in stock — it's delivered straight from stock, with no production or
                supplier wait. (Normally {strategyLabel(m.category.fulfillmentStrategy).toLowerCase()}.)
              </p>
            </div>
          )}

          {/* CTA — add to the shared cart, then send it to an order or a quote */}
          <div className="space-y-2 pt-2">
            <Button
              size="lg"
              className="w-full"
              disabled={priceUnset || !selected}
              onClick={onAdd}
            >
              {priceUnset
                ? 'Set a price to sell'
                : inCart
                  ? `✓ ${inCart.qty} in cart · Add one more`
                  : outOfStockEverywhere
                    ? 'Reserve — out of stock'
                    : '+ Add to cart'}
            </Button>
            {m.allowsCustomSize && (
              <Button
                size="lg"
                variant="secondary"
                className="w-full"
                onClick={() => setCustomOpen(true)}
              >
                + Custom size
              </Button>
            )}
            {outOfStockEverywhere && (
              <div className="text-xs text-ink-2 bg-info-soft border border-info/30 rounded px-2.5 py-2">
                Out of stock everywhere — adding to cart reserves it against incoming stock. The
                order is delivery-only and the delivery date is set when the stock arrives.
                Quotations still require stock on hand.
              </div>
            )}
            {items.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                <Link to="/orders/new" className="block">
                  <Button size="lg" className="w-full">
                    Create order →
                  </Button>
                </Link>
                <Link to="/quotations/new" className="block">
                  <Button size="lg" variant="secondary" className="w-full">
                    Create quote →
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {customOpen && (
        <CustomSizeModal
          modelId={m.id}
          fallbackName={m.name}
          fabric={selFabric}
          color={selAxisColor}
          sideColor={selAxisSide}
          density={selAxisDensity}
          onClose={() => setCustomOpen(false)}
          onConfirm={(r) => {
            cart.add({
              productId: `custom-${crypto.randomUUID()}`,
              sku: `Priced as ${r.matchedSize}`,
              name: `${r.modelName} — Custom ${r.widthCm}×${r.lengthCm} cm`,
              unitPrice: r.unitPrice,
              vatRate: r.vatRate,
              imageUrl: m.images[0]?.url ?? null,
              categoryName: m.category.name,
              colorOptions: r.colorOptions,
              sideColorOptions: r.sideColorOptions,
              fabricOptions: r.fabricOptions,
              densityOptions: r.densityOptions,
              selectedColor: r.selectedColor,
              selectedSideColor: r.selectedSideColor,
              selectedFabric: r.selectedFabric,
              selectedDensity: r.selectedDensity,
              deliveryRequired: r.deliveryRequired,
              fulfillmentStrategy: 'MADE_TO_ORDER_FACTORY',
              isCustom: true,
              matchedVariantId: r.matchedVariantId,
              customWidthCm: r.widthCm,
              customLengthCm: r.lengthCm,
              matchedSize: r.matchedSize,
            });
            setCustomOpen(false);
          }}
        />
      )}

      {m.description && (
        <Card title="Description">
          <p className="text-sm text-ink-2 whitespace-pre-line leading-relaxed">{m.description}</p>
        </Card>
      )}

      {attrEntries.length > 0 && (
        <Card title="Specifications">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {attrEntries.map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-rule-2 pb-2"
              >
                <dt className="text-ink-3 capitalize">{k.replace(/_/g, ' ')}</dt>
                <dd className="text-ink text-right">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
    </PageScaffold>
  );
}

// Inline-editable model field (gift / warranty) — saved via PATCH /product-models/:id.
function EditableModelField({
  modelId,
  field,
  label,
  value,
  canEdit,
  placeholder,
}: {
  modelId: string;
  field: 'gift' | 'warranty';
  label: string;
  value: string | null;
  canEdit: boolean;
  placeholder?: string;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const save = useMutation({
    mutationFn: () => apiPatch(`/product-models/${modelId}`, { [field]: val.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-model', modelId] });
      qc.invalidateQueries({ queryKey: ['product-models'] });
      setEditing(false);
    },
  });
  return (
    <div className="rounded-md border border-accent/30 bg-accent-soft/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">
          {label}
        </div>
        {canEdit && !editing && (
          <Button
            variant="tertiary"
            size="xs"
            onClick={() => {
              setVal(value ?? '');
              setEditing(true);
            }}
          >
            {value ? 'Edit' : 'Add'}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} />
          {save.isError && (
            <div className="text-xs text-negative">{getApiError(save.error).message}</div>
          )}
          <div className="flex gap-2">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-ink mt-1">
          {value ?? <span className="text-ink-3 italic">Not set</span>}
        </div>
      )}
    </div>
  );
}

function Swatches({
  label,
  options,
  value,
  onChange,
  isUnavailable,
}: {
  label: string;
  options: string[];
  value: string | null;
  onChange: (v: string | null) => void;
  /** Dim values that don't combine with the other selected axes. Still
   *  clickable — the parent switches the incompatible axes over. */
  isUnavailable?: (v: string) => boolean;
}): JSX.Element {
  return (
    <div>
      <div className="text-[11px] text-ink-3 mb-1.5">
        {label}
        {value && <span className="text-ink"> · {value}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((c) => {
          const dimmed = c !== value && !!isUnavailable?.(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(value === c ? null : c)}
              title={
                value === c
                  ? 'Click to unselect'
                  : dimmed
                    ? 'Not in this combination — click to switch the other options'
                    : undefined
              }
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs transition',
                value === c
                  ? 'border-accent bg-accent-soft text-accent font-medium'
                  : dimmed
                    ? 'border-rule-2 bg-surface text-ink-4 opacity-50 hover:opacity-100 hover:border-accent/40'
                    : 'border-rule bg-surface text-ink-2 hover:border-accent/40',
              )}
            >
              <span
                className="w-3.5 h-3.5 rounded-full border border-rule"
                style={{ backgroundColor: swatch(c) }}
              />
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}
